/**
 * LeadsPipelineView — Feb 2026
 *
 * Mobile-first kanban-by-status for the Leads module. Each column is a
 * configured workspace lead-status (ordered by `statusKeys`). Cards
 * live under their current status. Phone reps advance a deal without
 * opening it:
 *
 *   • Swipe RIGHT → next status
 *   • Swipe LEFT  → previous status
 *   • Long-press  → menu to pick any status
 *
 * Status updates are optimistic — the card slides out of the column
 * immediately. If the PATCH fails the move is rolled back and a toast
 * surfaces the server error.
 */
import { useMemo, useState, useCallback } from "react";
import { Flame, Phone, Mail, Loader2, MessageCircle, MoreVertical, Briefcase } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { currencySymbol, fmtMoney } from "@/lib/currency";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Oct 2026 — swipe constants removed; moving a lead now goes through
// the explicit "⋮" menu to avoid accidental status changes during
// normal mobile horizontal scrolling.

/**
 * Compact money formatter for the kanban column header. Keeps the
 * number short enough to fit a 320 px column:
 *   • ≥ 1 Cr → "1.23 Cr"
 *   • ≥ 1 L  → "4.56 L"
 *   • ≥ 1 k  → "78.9k"
 *   • else   → the raw rounded integer
 * Always prefixed with the currency symbol.
 */
function fmtPipelineTotal(amount, currency = "INR") {
  const n = Number(amount || 0);
  const sym = currencySymbol(currency);
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e7) return `${sign}${sym}${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}${sym}${(abs / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(1)}k`;
  return `${sign}${sym}${Math.round(abs).toLocaleString("en-IN")}`;
}

/**
 * Sum a list of leads' estimated_value, bucketed by currency code.
 * Returns the dominant currency bucket + a flag if other currencies
 * are present so the header can show e.g. "₹45.6L  +2 other".
 */
function summariseColumn(list) {
  const bucket = {}; // { INR: {total, count}, USD: {...} }
  let withValueCount = 0;
  (list || []).forEach((l) => {
    const amt = Number(l?.estimated_value);
    if (!amt || Number.isNaN(amt)) return;
    const cur = l?.currency || "INR";
    if (!bucket[cur]) bucket[cur] = { total: 0, count: 0 };
    bucket[cur].total += amt;
    bucket[cur].count += 1;
    withValueCount += 1;
  });
  const keys = Object.keys(bucket);
  if (keys.length === 0) return { total: 0, currency: null, otherCount: 0, withValueCount: 0 };
  // Pick the currency with the largest absolute total.
  keys.sort((a, b) => Math.abs(bucket[b].total) - Math.abs(bucket[a].total));
  const topCur = keys[0];
  return {
    total: bucket[topCur].total,
    currency: topCur,
    otherCount: keys.length - 1,
    withValueCount,
  };
}

export default function LeadsPipelineView({
  rows = [],
  loading = false,
  statusKeys = [],
  statusLabelMap = {},
  statusColorMap = {},
  onRowClick,
  onStatusChanged,
  emptyTitle = "No leads",
  emptyDescription = "",
}) {
  // Group rows by status key, preserving order.
  const groups = useMemo(() => {
    const g = {};
    statusKeys.forEach((k) => { g[k] = []; });
    (rows || []).forEach((r) => {
      const k = r.status || statusKeys[0];
      if (!g[k]) g[k] = [];
      g[k].push(r);
    });
    return g;
  }, [rows, statusKeys]);

  // Local "moving" set so swiped cards fade out instantly even before
  // the parent splice lands.
  const [movingIds, setMovingIds] = useState(() => new Set());

  const doMove = useCallback(async (lead, toStatus) => {
    if (!lead?.id || !toStatus || toStatus === lead.status) return;
    setMovingIds((prev) => {
      const n = new Set(prev); n.add(lead.id); return n;
    });
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { status: toStatus });
      onStatusChanged?.(lead.id, toStatus, data || null);
      toast.success(`Moved to ${statusLabelMap[toStatus] || toStatus}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Status change failed"));
    } finally {
      setMovingIds((prev) => {
        const n = new Set(prev); n.delete(lead.id); return n;
      });
    }
  }, [onStatusChanged, statusLabelMap]);

  // Grand total across the whole visible pipeline. Groups by currency
  // (most Indian workspaces will be all-INR but overseas deals are
  // common enough to deserve a per-currency breakdown instead of a
  // silently-wrong sum).
  const grand = useMemo(() => {
    const bucket = {};
    (rows || []).forEach((l) => {
      const amt = Number(l?.estimated_value);
      if (!amt || Number.isNaN(amt)) return;
      const cur = l?.currency || "INR";
      bucket[cur] = (bucket[cur] || 0) + amt;
    });
    return bucket;
  }, [rows]);

  if (loading && rows.length === 0) {
    return (
      <div className="border border-border bg-background p-8 text-center text-sm text-muted-foreground" data-testid="leads-pipeline-loading">
        <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
        Loading pipeline…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-border bg-background p-10 text-center" data-testid="leads-pipeline-empty">
        <Briefcase className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-60" />
        <div className="text-sm font-semibold mb-1">{emptyTitle}</div>
        {emptyDescription ? <div className="text-xs text-muted-foreground">{emptyDescription}</div> : null}
      </div>
    );
  }

  return (
    <div data-testid="leads-pipeline-wrap">
      {/* Grand total — one chip per currency so a mixed-currency
          pipeline (INR + USD deals) still gives an honest answer.
          Also summarises the count of leads with no estimated value
          so reps know how much pipeline isn't yet quantified. */}
      {rows.length > 0 && (
        <div
          className="mb-2 flex items-center flex-wrap gap-2 text-[11px]"
          data-testid="leads-pipeline-grand-total"
        >
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Pipeline value
          </span>
          {Object.keys(grand).length === 0 ? (
            <span className="text-muted-foreground italic">
              No estimated values set yet
            </span>
          ) : (
            Object.entries(grand).map(([cur, total]) => (
              <span
                key={cur}
                className="inline-flex items-center gap-1 border border-primary/40 bg-primary/5 text-primary px-2 py-0.5 font-mono"
                title={fmtMoney(total, cur)}
                data-testid={`leads-pipeline-grand-${cur}`}
              >
                <span className="text-[9px] uppercase tracking-widest opacity-70">{cur}</span>
                {fmtPipelineTotal(total, cur)}
              </span>
            ))
          )}
          <span className="text-muted-foreground">· {rows.length} {rows.length === 1 ? "lead" : "leads"}</span>
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-3 px-3 sm:mx-0 sm:px-0" data-testid="leads-pipeline">
        {statusKeys.map((key) => {
          const list = groups[key] || [];
          const color = statusColorMap[key];
          const summary = summariseColumn(list);
          return (
            <div
              key={key}
              className="shrink-0 w-[80vw] max-w-[320px] sm:w-72 md:w-80 bg-muted/30 border border-border flex flex-col"
              data-testid={`leads-pipeline-col-${key}`}
            >
              <div
                className="sticky top-0 z-10 px-3 py-2 bg-background border-b border-border"
                style={color ? { borderTopColor: color, borderTopWidth: 3 } : undefined}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} aria-hidden />}
                    <span className="text-xs font-semibold uppercase tracking-widest truncate">
                      {statusLabelMap[key] || key}
                    </span>
                  </div>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 border border-border bg-background text-muted-foreground">
                    {list.length}
                  </span>
                </div>
                {/* Per-column estimated-value total. Rendered under the
                    label so even a narrow 240 px column can display
                    the money without pushing the count off-screen. */}
                <div
                  className="mt-1 flex items-center justify-between text-[10px]"
                  data-testid={`leads-pipeline-total-${key}`}
                >
                  {summary.currency ? (
                    <span
                      className="font-mono text-foreground"
                      title={fmtMoney(summary.total, summary.currency)}
                      data-testid={`leads-pipeline-total-value-${key}`}
                    >
                      {fmtPipelineTotal(summary.total, summary.currency)}
                      {summary.otherCount > 0 ? (
                        <span className="ml-1 text-muted-foreground">+{summary.otherCount} other</span>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-muted-foreground italic">No value</span>
                  )}
                  {summary.withValueCount > 0 && summary.withValueCount < list.length && (
                    <span className="text-muted-foreground" title={`${list.length - summary.withValueCount} of ${list.length} have no estimated value`}>
                      {summary.withValueCount}/{list.length}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]">
                {list.length === 0 && (
                  <div className="text-[11px] text-muted-foreground italic text-center py-6 border border-dashed border-border/60 bg-background/40">
                    No leads here
                  </div>
                )}
                {list.map((lead) => (
                  <PipelineCard
                    key={lead.id}
                    lead={lead}
                    statusKeys={statusKeys}
                    statusLabelMap={statusLabelMap}
                    statusColorMap={statusColorMap}
                    busy={movingIds.has(lead.id)}
                    onOpen={() => onRowClick?.(lead)}
                    onMove={(toStatus) => doMove(lead, toStatus)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** One kanban card. Oct 2026 polish — swipe gestures were removed
 *  because horizontal column scrolling kept triggering accidental
 *  status changes. Users now move a lead through the explicit "⋮"
 *  menu button on the top-right of the card, and call / email /
 *  WhatsApp are first-class action buttons. */
function PipelineCard({
  lead, statusKeys, statusLabelMap, statusColorMap,
  busy, onOpen, onMove,
}) {
  const color = statusColorMap[lead.status];
  const hot = String(lead.temperature_manual || lead.temperature_ai || "").toLowerCase() === "hot";
  const phone = lead.phone || lead.mobile || lead.office_phone;
  const telHref = phone ? `tel:${String(phone).replace(/[^\d+]/g, "")}` : "";
  const waHref = phone
    ? `https://wa.me/${String(phone).replace(/[^\d+]/g, "").replace(/^\+/, "")}`
    : "";
  const mailHref = lead.email ? `mailto:${lead.email}` : "";
  // Stop a child button's click from bubbling into the card wrapper
  // (which would open the lead dialog).
  const stop = (e) => { e.stopPropagation(); };

  return (
    <div
      onClick={() => onOpen?.()}
      className={`relative bg-background border border-border p-2.5 cursor-pointer hover:bg-muted/20 active:bg-muted/40 ${busy ? "opacity-60 pointer-events-none" : ""}`}
      data-testid={`lead-pipeline-card-${lead.id}`}
    >
      {color && (
        <span className="absolute top-0 left-0 w-1 h-full" style={{ background: color }} aria-hidden />
      )}
      <div className="flex items-start justify-between gap-2 pl-1.5">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate flex items-center gap-1.5">
            {lead.name || "—"}
            {hot && <Flame className="w-3.5 h-3.5 text-rose-500 shrink-0" />}
          </div>
          {lead.company ? (
            <div className="text-[11px] text-muted-foreground truncate">
              {lead.company}{lead.title ? ` · ${lead.title}` : ""}
            </div>
          ) : lead.title ? (
            <div className="text-[11px] text-muted-foreground truncate">{lead.title}</div>
          ) : null}
        </div>
        {busy ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground shrink-0" />
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                onClick={stop}
                className="p-1 -mr-1 -mt-1 text-muted-foreground hover:text-foreground hover:bg-muted/40 border border-transparent hover:border-border"
                aria-label="Lead actions"
                data-testid={`lead-pipeline-menu-btn-${lead.id}`}
              >
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid={`lead-pipeline-menu-${lead.id}`}>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Move to status
              </DropdownMenuLabel>
              {statusKeys.map((k) => (
                <DropdownMenuItem
                  key={k}
                  disabled={k === lead.status}
                  onClick={() => onMove(k)}
                  data-testid={`lead-pipeline-menu-${lead.id}-${k}`}
                >
                  {statusColorMap[k] && (
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: statusColorMap[k] }} aria-hidden />
                  )}
                  {statusLabelMap[k] || k}
                  {k === lead.status && <span className="ml-auto text-[9px] text-muted-foreground">current</span>}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Contact
              </DropdownMenuLabel>
              {telHref && (
                <DropdownMenuItem onClick={stop} asChild data-testid={`lead-pipeline-menu-${lead.id}-call`}>
                  <a href={telHref}>
                    <Phone className="w-3.5 h-3.5 mr-2 text-sky-600" /> Call {phone}
                  </a>
                </DropdownMenuItem>
              )}
              {waHref && (
                <DropdownMenuItem onClick={stop} asChild data-testid={`lead-pipeline-menu-${lead.id}-wa`}>
                  <a href={waHref} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-3.5 h-3.5 mr-2 text-emerald-600" /> WhatsApp
                  </a>
                </DropdownMenuItem>
              )}
              {mailHref && (
                <DropdownMenuItem onClick={stop} asChild data-testid={`lead-pipeline-menu-${lead.id}-email`}>
                  <a href={mailHref}>
                    <Mail className="w-3.5 h-3.5 mr-2 text-blue-600" /> Email
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onOpen?.()} data-testid={`lead-pipeline-menu-${lead.id}-open`}>
                Open lead…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {/* Primary contact row — compact action buttons. Still clickable
          even when the card itself routes to the dialog on tap. */}
      {(telHref || waHref || mailHref) && (
        <div className="mt-1.5 pl-1.5 flex items-center gap-1.5" data-testid={`lead-pipeline-actions-${lead.id}`}>
          {telHref && (
            <a href={telHref} onClick={stop}
                className="inline-flex items-center justify-center h-7 w-7 border border-border text-sky-600 hover:bg-sky-50"
                title={`Call ${phone}`}
                data-testid={`lead-pipeline-call-${lead.id}`}>
              <Phone className="w-3.5 h-3.5" />
            </a>
          )}
          {waHref && (
            <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={stop}
                className="inline-flex items-center justify-center h-7 w-7 border border-border text-emerald-600 hover:bg-emerald-50"
                title="Open WhatsApp"
                data-testid={`lead-pipeline-wa-${lead.id}`}>
              <MessageCircle className="w-3.5 h-3.5" />
            </a>
          )}
          {mailHref && (
            <a href={mailHref} onClick={stop}
                className="inline-flex items-center justify-center h-7 w-7 border border-border text-blue-600 hover:bg-blue-50"
                title={`Email ${lead.email}`}
                data-testid={`lead-pipeline-email-${lead.id}`}>
              <Mail className="w-3.5 h-3.5" />
            </a>
          )}
          <span className="ml-1 text-[11px] text-muted-foreground truncate">
            {phone || lead.email}
          </span>
        </div>
      )}
      {lead.next_followup_at && (
        <div className="mt-1 pl-1.5 text-[10px] text-muted-foreground">
          Next: {String(lead.next_followup_at).slice(0, 10)}
        </div>
      )}
    </div>
  );
}
